import { defineQuiz } from "./_schema";
import { cortisolQuiz } from "./cortisol";

/**
 * CORTISOL-QUIZ-EG-X -- the cortisol quiz with the in-loader EMAIL GATE, on its
 * own slug for X (Twitter) traffic.
 *
 * Two things at once, and they are separable:
 *
 *  1. THE GATE. `emailGate: true` turns on the hard email capture embedded in
 *     the `applying` loader (see PersonaQuiz.emailGate in _schema.ts) -- email
 *     is required to reach the PDP, there is no skip, and the submission POST is
 *     deferred to the gate so the captured address lands on the Klaviyo row.
 *     Same Variant C gate quiz-v6-gated runs on quiz-v6; cortisol had none until
 *     now. The seam is generic and already live, so no component work: the gate
 *     hooks the standard running -> building -> result -> projection -> offer ->
 *     applying -> PDP endgame, which cortisol uses because it declares no
 *     `postQuiz`.
 *  2. THE SLUG. Analytics here is keyed by SLUG, not URL (admin funnel,
 *     quiz_submissions.quiz_slug, Klaviyo event/profile props), so X-sourced
 *     traffic to the gated cortisol funnel is a distinct row everywhere in our
 *     own analytics. Published at /pages/cortisol-quiz-eg-x.
 *
 * WHY THE GATE IS DECLARED HERE rather than inherited. quiz-v6-eg-x -- the
 * equivalent surface on the quiz-v6 side -- spreads quiz-v6-gated, a two-hop
 * chain, specifically so `emailGate` is inherited and parent and clone can never
 * disagree about whether the surface is gated. That reasoning needs a gated
 * parent, and cortisol has none: the operator chose X-only on 2026-09-02, so
 * this is the ONLY gated cortisol surface and there is nothing to inherit the
 * flag from. Declaring it directly off cortisol is exactly the shape
 * quiz-v6-gated has off quiz-v6. Do not manufacture an intermediate
 * general-traffic quiz to imitate the two-hop chain. IF a general-traffic gated
 * cortisol is ever added, re-point this file at it in that same PR so the flag
 * goes back to being inherited, rather than leaving two independent
 * `emailGate: true` declarations to drift apart.
 *
 * NAMING: slug == page handle == `cortisol-quiz-eg-x`, deliberately. The channel
 * suffix goes on the page-handle stem for every sibling (`quiz-v6-x` off
 * /pages/quiz-v6, `cortisol-quiz-x` off /pages/cortisol-quiz), but there is no
 * gated-cortisol page to take a stem from, so the convention does not settle it
 * and the operator chose this spelling on 2026-09-02. `eg` is the same
 * email-gated marker /pages/quiz-v6-eg carries. The v6 family's slug/handle
 * MISMATCH (slug `quiz-v6-gated`, handle `quiz-v6-eg`) is an artifact of a
 * 2026-07-20 rename that now needs explaining in two places -- it is not a
 * pattern, and it is not reproduced here.
 *
 * Everything that routes or renders -- `questions`, `branching`, `classifier`,
 * `deriveProfile` (deriveCortisolProfile, incl. the cortisol-dominance
 * forced-Primary seam), `meta` -- is spread from `cortisolQuiz` and shared BY
 * REFERENCE, so this quiz can never drift from cortisol: any future edit
 * propagates automatically. `emailGate` is a runtime-render flag that never
 * touches `branching`, `classifier` or `deriveProfile`, which is the whole
 * reason it is a flag rather than schema -- so this surface stays
 * regression-identical to cortisol and its baseline is CORTISOL_BASELINE by
 * construction. Do NOT edit routing or copy here; edit `cortisol.ts` (this clone
 * inherits it) or fork the shared references if the two surfaces must diverge.
 *
 * Intelligems: NO experiment reaches this page. It spreads cortisol and carries
 * no `experiment: undefined` override, so it INHERITS whatever cortisol
 * declares -- which is nothing, since MM-0093 concluded 2026-09-01 with the
 * promoted-angle arm losing and was removed from the parent. The page therefore
 * touches no `window.igData` and pays no resolver poll (rule 24). Inheritance
 * stays the default for the next test, matching cortisol-quiz-x and quiz-v6-x:
 * excluding the clones would leave them serving a different screen order from
 * their parent. Opting this slug out of a future cortisol test means adding
 * `experiment: undefined` here.
 *
 * See docs/specs/2026-09-02-cortisol-quiz-eg-x-design.md.
 */
export const cortisolQuizEgXQuiz = defineQuiz({
  ...cortisolQuiz,
  id: "cortisol-quiz-eg-x",
  slug: "cortisol-quiz-eg-x",
  emailGate: true,
});
